import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { api, ApiError } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/auth-store';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

/**
 * Wire response shape from POST /api/auth/admin/signin and POST /api/auth/signin/2fa.
 *
 * Admin signin is *always* 2FA-gated server-side (docs/27 §7) — the first call
 * returns a ticket, the second exchanges ticket+TOTP for tokens.  Customer-side
 * shapes are no longer used here; admin doesn't share the customer signin path.
 */
interface TwoFactorRequiredResponse {
  twoFactorRequired: true;
  ticket: string;
}

interface SigninResponse {
  user: { id: string; email: string; isSuperadmin: boolean };
  accessToken: string;
}

/** Default access-token lifetime (matches JWT_ACCESS_TTL=15m server-side). */
const ACCESS_TTL_MS = 15 * 60 * 1000;

type Stage =
  | { kind: 'credentials' }
  | { kind: 'two_factor'; ticket: string };

function LoginPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmitCredentials(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await api<TwoFactorRequiredResponse>('/api/auth/admin/signin', {
        method: 'POST',
        body: { email, password },
        unauthenticated: true,
      });
      // The admin endpoint always returns a ticket on success — the gate
      // checks `is_superadmin` + `two_factor_enabled` server-side before
      // issuing one.  Non-admins / un-enrolled superadmins hit the catch
      // branch with a typed error code below.
      if (!('twoFactorRequired' in result) || !result.twoFactorRequired) {
        toast.error('Unexpected response from /api/auth/admin/signin');
        return;
      }
      setStage({ kind: 'two_factor', ticket: result.ticket });
      setCode('');
    } catch (err) {
      handleSigninError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (stage.kind !== 'two_factor') return;
    setSubmitting(true);
    try {
      const result = await api<SigninResponse>('/api/auth/signin/2fa', {
        method: 'POST',
        body: { ticket: stage.ticket, code },
        unauthenticated: true,
      });
      // Defense in depth — the admin endpoint already gated on
      // is_superadmin before issuing the ticket, but the 2FA exchange step
      // is shared with customer signin so re-verify before unlocking the
      // admin UI.
      if (!result.user.isSuperadmin) {
        toast.error('This account is not a superadmin', {
          description: 'Ask an existing admin to promote you via pnpm admin:promote.',
        });
        return;
      }
      // The refresh token is now set by the API as an httpOnly cookie
      // (`projoint_admin_rt`); we only keep the in-memory access token.
      useAuthStore.getState().setTokens({
        accessToken: result.accessToken,
        accessExpiresAt: Date.now() + ACCESS_TTL_MS,
      });
      useAuthStore.getState().setAuthenticated(true);
      await navigate({ to: '/' });
    } catch (err) {
      handleSigninError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSigninError(err: unknown): void {
    if (err instanceof ApiError) {
      // The admin signin gate uses distinct codes — surface each with copy
      // that's actionable.  All other codes (bad password, banned, invalid
      // ticket, rate-limit) fall through to the generic branch.
      if (err.code === 'not_superadmin') {
        toast.error('Not a superadmin account', {
          description:
            typeof err.body.error.message === 'string'
              ? err.body.error.message
              : 'Ask an existing operator to promote you (pnpm admin:promote).',
        });
        return;
      }
      if (err.code === 'superadmin_not_enrolled') {
        toast.error('2FA is required for operator accounts', {
          description:
            'Sign in to the customer app first, enable 2FA in Account settings → Account, then return here.',
        });
        return;
      }
      if (err.code === 'account_banned') {
        toast.error('Account banned', {
          description:
            typeof err.body.error.reason === 'string' ? err.body.error.reason : undefined,
        });
        return;
      }
      if (err.code === 'invalid_ticket') {
        // Ticket expired — bounce the user back to the credentials stage.
        setStage({ kind: 'credentials' });
        toast.error('Session expired — sign in again');
        return;
      }
      toast.error(err.body.error.message ?? 'Sign-in failed');
      return;
    }
    toast.error(err instanceof Error ? err.message : 'Sign-in failed');
  }

  return (
    <div className="grid min-h-screen place-items-center bg-neutral-950">
      <form
        onSubmit={stage.kind === 'credentials' ? onSubmitCredentials : onSubmitCode}
        className="w-full max-w-sm rounded-xl bg-neutral-900 p-8 shadow-xl ring-1 ring-neutral-800"
      >
        <h1 className="mb-1 text-xl font-semibold text-neutral-100">Projoint · Admin</h1>
        <p className="mb-6 text-sm text-neutral-400">
          {stage.kind === 'credentials'
            ? 'Operator console.  Superadmin access only.'
            : `Enter the 6-digit code from your authenticator app.`}
        </p>

        {stage.kind === 'credentials' ? (
          <>
            <label
              className="mb-2 block text-xs uppercase tracking-wider text-neutral-400"
              htmlFor="email"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mb-4 w-full rounded-md bg-neutral-800 px-3 py-2 text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />

            <label
              className="mb-2 block text-xs uppercase tracking-wider text-neutral-400"
              htmlFor="password"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mb-6 w-full rounded-md bg-neutral-800 px-3 py-2 text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />

            <button
              type="submit"
              disabled={submitting || !email || !password}
              className="w-full rounded-md bg-orange-600 px-4 py-2 font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Signing in…' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <label
              className="mb-2 block text-xs uppercase tracking-wider text-neutral-400"
              htmlFor="totp"
            >
              Authenticator code
            </label>
            <input
              id="totp"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
              className="mb-6 w-full rounded-md bg-neutral-800 px-3 py-2 text-center font-mono text-2xl tracking-[0.5em] text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
              placeholder="000000"
            />

            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="mb-2 w-full rounded-md bg-orange-600 px-4 py-2 font-medium text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStage({ kind: 'credentials' });
                setCode('');
              }}
              className="w-full rounded-md px-4 py-2 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
            >
              ← Use a different account
            </button>
          </>
        )}
      </form>
    </div>
  );
}
